import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../services/session-storage.service';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { LocationMergePair, LocationMergePreview, LocationMergeReference, LocationMergeService } from './location-merge.service';

@Component({
    selector: 'app-location-merge', standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, CheckboxModule, InputTextModule, SelectModule,
        TableModule, ToastModule, PageShellComponent],
    providers: [MessageService],
    templateUrl: './location-merge.component.html', styleUrl: './location-merge.component.css'
})
export class LocationMergeComponent implements OnInit {
    private service = inject(LocationMergeService);
    private session = inject(SessionStorageService);
    private permissions = inject(PermissionService);
    private messages = inject(MessageService);
    readonly canMerge = this.permissions.hasPermission(app_permissions.location.records.edit)
        && this.permissions.hasPermission(app_permissions.location.records.delete);
    companyId = 0;
    searchText = '';
    manualSearch = '';
    manualOptions: { label: string; value: number }[] = [];
    firstId: number | null = null;
    secondId: number | null = null;
    page = 0;
    candidates: LocationMergePair[] = [];
    preview: LocationMergePreview | null = null;
    sources: Record<string, number> = {};
    contactIds: number[] = [];
    merchandiserIds: number[] = [];
    confirmed = false;
    loading = false;
    saving = false;

    ngOnInit(): void {
        this.companyId = this.session.getCompany()?.id || 0;
        if (this.canMerge && this.companyId) this.loadCandidates();
    }
    loadCandidates(): void {
        this.loading = true;
        this.service.candidates(this.companyId, this.searchText, this.page).pipe(finalize(() => this.loading = false)).subscribe({
            next: rows => this.candidates = rows,
            error: () => this.error('Could not load location suggestions.')
        });
    }
    searchLocations(): void {
        if (this.manualSearch.trim().length < 2) return;
        this.service.search(this.companyId, this.manualSearch).subscribe({
            next: rows => {
                const retained = this.manualOptions.filter(option => option.value === this.firstId || option.value === this.secondId);
                const found = rows.map(row => ({ value: row.id,
                    label: [row.name, row.reference, row.address, `#${row.id}`].filter(Boolean).join(' | ') }));
                this.manualOptions = [...retained, ...found.filter(option => !retained.some(old => old.value === option.value))];
            },
            error: () => this.error('Could not search locations.')
        });
    }
    compare(primaryId: number, duplicateId: number): void {
        if (!primaryId || !duplicateId || primaryId === duplicateId) {
            this.error('Choose two different locations.');
            return;
        }
        this.loading = true;
        this.confirmed = false;
        this.service.preview(this.companyId, primaryId, duplicateId).pipe(finalize(() => this.loading = false)).subscribe({
            next: result => {
                this.preview = result;
                this.sources = Object.fromEntries(result.fields.map(field => [field, primaryId]));
                this.contactIds = result.contacts.map(row => Number(row['id']));
                this.merchandiserIds = result.merchandisers.map(row => Number(row['id']));
            },
            error: () => this.error('Could not inspect both locations and their usage.')
        });
    }
    changeSource(field: string, id: number): void {
        this.sources[field] = id;
        if (field === 'latitude') this.sources['longitude'] = id;
        this.confirmed = false;
    }
    fieldLabel(field: string): string {
        const names: Record<string, string> = { externalmessage: 'Reference', externallocationreference: 'Reference',
            countryid: 'Country', cityid: 'City', locationzoneid: 'Location zone', retailerid: 'Retailer',
            defaultcontactid: 'Default contact', latitude: 'Coordinates', normalizeddistrict: 'Normalized district',
            longdescription: 'Long description', shortdescription: 'Short description', telephonenumber: 'Telephone',
            placeid: 'Place ID', roomnumber: 'Room', floornumber: 'Floor', buildingnumber: 'Building',
            nearbylandmark: 'Nearby landmark', frontdoorimage: 'Front door image', buildingimage: 'Building image',
            locationtype: 'Location type', helperimage1: 'Helper image 1', helperimage2: 'Helper image 2', helperimage3: 'Helper image 3' };
        return names[field] || field.charAt(0).toUpperCase() + field.slice(1);
    }
    fieldValue(row: Record<string, any>, field: string): string {
        if (field === 'latitude') return `${row['latitude'] ?? '—'}, ${row['longitude'] ?? '—'}`;
        if (field === 'cityid' && row['cityname']) return `${row['cityname']} (#${row[field]})`;
        if (field === 'retailerid' && row['retailername']) return `${row['retailername']} (#${row[field]})`;
        return row[field] == null || row[field] === '' ? '—' : String(row[field]);
    }
    referenceLabel(reference: LocationMergeReference): string {
        const labels: Record<string, string> = {
            'posm.posmprojectstores': 'POSM target stores', 'posm.posmdeliveryschedules': 'POSM delivery schedules',
            'posm.posminstallationtasks': 'POSM tasks', 'trxn.shipment': 'Shipments',
            'warehousing.warehouses': 'Warehouses', 'entity.locationcontacts': 'Contacts',
            'entity.locationmerchandisers': 'Merchandiser assignments'
        };
        const suffix = reference.column.includes('source') ? ' · origin'
            : reference.column.includes('destination') ? ' · destination'
            : reference.column.includes('return') ? ' · return' : '';
        return (labels[reference.table] || 'Other linked records') + suffix;
    }
    setSelected(kind: 'contact' | 'merchandiser', id: number, checked: boolean): void {
        const current = kind === 'contact' ? this.contactIds : this.merchandiserIds;
        const next = checked ? Array.from(new Set([...current, id])) : current.filter(value => value !== id);
        if (kind === 'contact') this.contactIds = next; else this.merchandiserIds = next;
        this.confirmed = false;
    }
    merge(): void {
        const preview = this.preview;
        if (!preview || !preview.mergeable || !this.confirmed || this.saving) return;
        this.saving = true;
        this.service.merge(this.companyId, { primaryId: Number(preview.primary['id']), duplicateId: Number(preview.duplicate['id']),
            fingerprint: preview.fingerprint, fieldSources: this.sources, contactIds: this.contactIds,
            merchandiserIds: this.merchandiserIds }).pipe(finalize(() => this.saving = false)).subscribe({
                next: result => {
                    this.preview = null;
                    this.confirmed = false;
                    this.messages.add({ severity: 'success', summary: 'Locations merged', detail: `Location #${result.locationId} was kept.` });
                    this.loadCandidates();
                },
                error: err => this.error(err?.error?.message || 'Merge failed. Review the locations and their linked records again.')
            });
    }
    private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'Location management', detail }); }
}
